import { CometChatCalls } from '@cometchat/calls-sdk-javascript';
import { CometChatUIKit } from '@cometchat/chat-uikit-react';
import { onAuthStateChanged } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import './App.css';
import { COMETCHAT_CONSTANTS } from './AppConstants';
import HistoryScreen from './History';
import Home from './Home';
import LoginWithFirebase from './LoginWithFirebase';
import { auth, signOutFirebase } from './firebase';
import { initCallsSDK } from './utils/init-calls-sdk';

const isE2ETest = import.meta.env.VITE_E2E_TEST === 'true';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isFirebaseReady, setIsFirebaseReady] = useState(false);

  useEffect(() => {
    const initSDK = async () => {
      try {
        const { error } = await initCallsSDK();

        if (error) {
          console.error('CometChat SDK initialization failed:', error);
          return;
        }

        setIsInitialized(true);
        console.log('Calls SDK initialized successfully');
      } catch (error) {
        console.error('CometChat SDK initialization failed:', error);
      }
    };

    initSDK();
  }, []);

  // In e2e mode, bypass Firebase and login directly via CometChat SDK
  useEffect(() => {
    if (!isE2ETest || !isInitialized) return;

    const e2eLogin = async () => {
      try {
        const uid =
          new URLSearchParams(window.location.search).get('e2e_uid') ||
          'cometchat-uid-1';
        await CometChatCalls.login(uid, COMETCHAT_CONSTANTS.AUTH_KEY);
        setIsLoggedIn(true);
      } catch (error) {
        console.error('E2E auto-login failed:', error);
      }
      setIsFirebaseReady(true);
    };

    e2eLogin();
  }, [isInitialized]);

  // Listen for Firebase authentication state changes (skip in e2e mode)
  useEffect(() => {
    if (isE2ETest) return;

    const unsubscribe = onAuthStateChanged(auth, user => {
      if (user) {
        const cometChatUser = CometChatCalls.getLoggedInUser();
        if (cometChatUser) {
          setIsLoggedIn(true);
        }
      } else {
        setIsLoggedIn(false);
      }
      setIsFirebaseReady(true);
    });

    return () => unsubscribe();
  }, []);

  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
  };

  const handleLogout = async () => {
    try {
      await CometChatCalls.logout();
      await CometChatUIKit.logout();
      await signOutFirebase();
      setIsLoggedIn(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Show loading while SDK is initializing
  if (!isInitialized || !isFirebaseReady) {
    return (
      <div className="container-app" data-theme={'dark'}>
        <div className="card loading-container">
          <div className="loading-spinner" />
          <h2 className="loading-title">Initializing CometChat</h2>
          <p className="loading-subtitle">
            Setting up your communication experience...
          </p>
        </div>
      </div>
    );
  }

  // Show Firebase login if not logged in
  if (!isLoggedIn) {
    return (
      <div data-theme={'dark'}>
        <LoginWithFirebase onLoginSuccess={handleLoginSuccess} />
      </div>
    );
  }

  // Main App UI when logged in
  return (
    <div data-theme={'dark'} style={{ height: '100%', width: '100%' }}>
      <Routes>
        <Route
          path="/history"
          element={<HistoryScreen onLogout={handleLogout} />}
        />
        <Route path="*" element={<Home onLogout={handleLogout} />} />
      </Routes>
    </div>
  );
}

export default App;
